import { ReactElement, useEffect, useState } from 'react';
import { Component as PCLogo } from 'consumer/components/logos/pclogo-black.svg';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { Drawer } from '../shared/drawer/Drawer';
import { SignupDrawerLayout } from './SignupDrawerLayout';
import { CreateAccount } from '../create-account/CreateAccount';
import { CreateAddress } from '../create-address/CreateAddress';
import { SignupStep } from '../../constants/signup';
import { Payment } from '../payment/Payment';
import { DisabledEmailSignup } from '../create-account/DisabledEmailSignup';

type Props = {
  setSignInModalVisible?: (val: boolean) => void;
  userAppliedCouponCode?: string;
}

function SignupDrawerContainer({
  setSignInModalVisible = undefined,
  userAppliedCouponCode = undefined,
}: Props): ReactElement {
  const {
    isCartDrawerOpen,
    setIsCartDrawerOpen,
    currentSignupStep,
    setCurrentSignupStep,
  } = useMenuWithCart();

  // Needed useState to persist coupon code through
  // to payment step otherwise wiped after account creation
  const [userCouponCode, _setUserCouponCode] = useState(userAppliedCouponCode);

  const accountCreationEmail = document.cookie
    .split('; ')
    .find((row) => row.startsWith('account_creation_email='))
    ?.split('=')[1];

  const email = accountCreationEmail ? decodeURIComponent(accountCreationEmail) : null;

  const handleClose = () => {
    setIsCartDrawerOpen(false);

    if (currentSignupStep === SignupStep.CREATE_ACCOUNT) {
      // if the user closes the drawer before completing the Create Account step,
      // reset them back to the cart
      setCurrentSignupStep(undefined);
    }
  };

  const handleClickLogin = () => {
    if (setSignInModalVisible) {
      setSignInModalVisible(true);
      handleClose();
    }
  };

  useEffect(() => {
    // disable scrolling of overlay when signup drawer is open
    if (isCartDrawerOpen) {
      document.body.classList.add('no-scroll-y');
    } else {
      document.body.classList.remove('no-scroll-y');
    }

    return () => {
      document.body.classList.remove('no-scroll-y');
    };
  }, [isCartDrawerOpen]);

  return (
    <Drawer
      trapFocus={false}
      isOpen={isCartDrawerOpen}
      onClose={handleClose}
      header={<PCLogo width="160px" focusable="false" />}
      isHeaderCentered
      content={(
        <SignupDrawerLayout>
          {currentSignupStep === SignupStep.CREATE_ACCOUNT && (
            <>
              {email ? (<DisabledEmailSignup />) : (
                <CreateAccount onClickLogin={handleClickLogin} />)}
            </>
          )}
          {currentSignupStep === SignupStep.SHIPPING_ADDRESS && (
            <CreateAddress />
          )}
          {currentSignupStep === SignupStep.PAYMENT && (
            <Payment handleClose={handleClose} couponCode={userCouponCode} />
          )}
        </SignupDrawerLayout>
      )}
      overrideStyles={{
        body: {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          padding: 0,
          overflow: 'hidden',
        },
        drawer: {
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    />
  );
}

export default SignupDrawerContainer;
